import { useEffect, useState } from 'react'
import {BookOpen, Plus, Search, Edit2, Trash2, CheckCircle} from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { useTrainings, TRAINING_TYPE_LABELS, TRAINING_MODALITY_LABELS, TRAINING_STATUS_META, type Training } from '@/hooks/useTrainings'
import { useAuth } from '@/hooks/useAuth'

const EMPTY: Omit<Training, '_id'> = {
  title: '', type: 'curso', status: 'borrador', description: '', instructor: '',
  modality: 'en_linea', duration: '', cost: 0, capacity: 0, enrolledCount: 0,
  startDate: '', endDate: '', tags: [], imageUrl: '', videoUrl: '', pdfUrl: '',
  location: '', creator: 'admin', createdAt: '', updatedAt: '',
}

function AdminCapacitaciones() {
  const { user } = useAuth()
  const { trainings, loading, fetchAll, createTraining, updateTraining, deleteTraining } = useTrainings()
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('todos')
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<Training | null>(null)
  const [form, setForm] = useState<Omit<Training, '_id'>>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Training | null>(null)

  useEffect(() => { fetchAll() }, [])

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000) }

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, creator: user?.userId ?? 'admin' }); setShowModal(true) }
  const openEdit = (t: Training) => { setEditing(t); setForm({ ...t }); setShowModal(true) }

  const handleSave = async () => {
    if (!form.title.trim()) return
    setSaving(true)
    try {
      if (editing) {
        await updateTraining(editing._id, form)
        showToast('Capacitación actualizada.')
      } else {
        await createTraining(form)
        showToast('Capacitación creada.')
      }
      await fetchAll()
      setShowModal(false)
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const handleDelete = async (t: Training) => {
    try { await deleteTraining(t._id); await fetchAll(); showToast('Eliminada correctamente.') } catch (e) { console.error(e) }
    setConfirmDelete(null)
  }

  const filtered = trainings.filter((t) => {
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase()) || t.instructor?.toLowerCase().includes(search.toLowerCase())
    const matchStatus = filterStatus === 'todos' || t.status === filterStatus
    return matchSearch && matchStatus
  })

  return (
    <AdminLayout title="Capacitaciones" subtitle="Publica y administra cursos, talleres, seminarios y certificaciones">
      {toast ? (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm text-white shadow-lg">
          <CheckCircle size={16} className="text-emerald-400" />{toast}
        </div>
      ) : null}

          <div className="mb-6 flex justify-end">
            <button onClick={openCreate} className="flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-500">
              <Plus size={16} /> Nueva capacitación
            </button>
          </div>

          <div className="mb-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por título o instructor…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100" />
            </div>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400">
              <option value="todos">Todos los estados</option>
              {Object.entries(TRAINING_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
              <BookOpen size={32} className="mb-3 text-slate-300" />
              <p className="font-semibold text-slate-600">Sin capacitaciones</p>
              <button onClick={openCreate} className="mt-4 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-500">Crear primera capacitación</button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Capacitación</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 sm:table-cell">Tipo</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 md:table-cell">Modalidad</th>
                    <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 lg:table-cell">Inscritos</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Estado</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((t) => {
                    const meta = TRAINING_STATUS_META[t.status]
                    return (
                      <tr key={t._id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{t.title}</p>
                          {t.instructor ? <p className="text-xs text-slate-400">{t.instructor}</p> : null}
                        </td>
                        <td className="hidden px-4 py-3 text-slate-500 sm:table-cell">{TRAINING_TYPE_LABELS[t.type]}</td>
                        <td className="hidden px-4 py-3 text-slate-500 md:table-cell">{t.modality ? TRAINING_MODALITY_LABELS[t.modality] : '—'}</td>
                        <td className="hidden px-4 py-3 text-slate-500 lg:table-cell">{t.enrolledCount ?? 0}</td>
                        <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.color}`}>{meta.label}</span></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEdit(t)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Edit2 size={14} /></button>
                            <button onClick={() => setConfirmDelete(t)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

      {/* Modal crear/editar */}
      {showModal ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">{editing ? 'Editar capacitación' : 'Nueva capacitación'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Título *</label>
                <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Tipo</label>
                  <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as Training['type'] }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400">
                    {Object.entries(TRAINING_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Estado</label>
                  <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as Training['status'] }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400">
                    {Object.entries(TRAINING_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Modalidad</label>
                  <select value={form.modality} onChange={(e) => setForm((f) => ({ ...f, modality: e.target.value as Training['modality'] }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400">
                    {Object.entries(TRAINING_MODALITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Costo (MXN)</label>
                  <input type="number" min="0" value={form.cost ?? 0} onChange={(e) => setForm((f) => ({ ...f, cost: Number(e.target.value) }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Instructor</label>
                  <input value={form.instructor ?? ''} onChange={(e) => setForm((f) => ({ ...f, instructor: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Duración</label>
                  <input value={form.duration ?? ''} onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Cupo máximo</label>
                  <input type="number" min="0" value={form.capacity ?? 0} onChange={(e) => setForm((f) => ({ ...f, capacity: Number(e.target.value) }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Fecha inicio</label>
                  <input type="date" value={form.startDate?.slice(0, 10) ?? ''} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value ? `${e.target.value}T09:00:00.000Z` : '' }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Fecha fin</label>
                  <input type="date" value={form.endDate?.slice(0, 10) ?? ''} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value ? `${e.target.value}T18:00:00.000Z` : '' }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Ubicación</label>
                  <input value={form.location ?? ''} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Descripción</label>
                <textarea rows={3} value={form.description ?? ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400 resize-none" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Etiquetas (separadas por coma)</label>
                <input value={(form.tags ?? []).join(', ')} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button onClick={handleSave} disabled={saving || !form.title.trim()} className="rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-500 disabled:opacity-60">
                {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear capacitación'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Confirm delete */}
      {confirmDelete ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-base font-bold text-slate-900">¿Eliminar capacitación?</h3>
            <p className="mb-5 text-sm text-slate-500">Se eliminará permanentemente <strong>"{confirmDelete.title}"</strong>. Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button onClick={() => handleDelete(confirmDelete)} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">Eliminar</button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  )
}

export default AdminCapacitaciones
